import React from 'react';
import {
  Box,
  Typography,
  Skeleton,
  Alert,
  useTheme,
  CircularProgress,
  Divider,
} from '@mui/material';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import ButtonMAIQ from 'app/components/ReusableUIComponents/LowLevelComponents/buttons/ButtonMAIQ';
import useMessageContent from './hooks/useMessageContent';
import InitializeMessageContent from './components/InitializeMessageContent';
import OperationAccordion from './components/OperationAccordion';

const MessageContentTab = () => {
  const theme = useTheme();
  const {
    messageContent,
    isLoading,
    isInitializing,
    error,
    handleInitialize,
    handleRetry,
    dealerId,
    allDealerValues,
    allDealerSpecials,
    allFooterImages,
    isLoadingAvailable,
    localOperations,
    isDirty,
    resetKey,
    isSavingAll,
    saveAllError,
    handleOperationFieldUpdate,
    handleSaveAll,
    handleCancelAll,
  } = useMessageContent();

  if (!dealerId) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
        }}
      >
        <Typography variant="h5" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
          Please select a dealer to view message content
        </Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ pt: 2 }}>
        {[1, 2, 3, 4, 5].map(i => (
          <Skeleton
            key={i}
            variant="rounded"
            height={64}
            sx={{ mb: 2, borderRadius: '10px' }}
          />
        ))}
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          gap: 2,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 520, width: '100%', borderRadius: 2 }}>
          {error}
        </Alert>
        <ButtonMAIQ variant="outlined" onClick={handleRetry}>
          Retry
        </ButtonMAIQ>
      </Box>
    );
  }

  if (!messageContent) {
    return (
      <InitializeMessageContent
        isInitializing={isInitializing}
        onInitialize={handleInitialize}
      />
    );
  }

  return (
    <Box sx={{ pb: 6 }}>
      {/* ── Sticky Header ── */}
      <Box
        sx={{
          position: 'sticky',
          top: '-24px',
          zIndex: 10,
          backgroundColor: theme.palette.background.paper,
          mx: -3,
          px: 3,
          mt: '-24px',
          pt: '40px',
          pb: 1.5,
          mb: 1,
          boxShadow: '0 8px 24px 0 rgba(0,0,0,0.18)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          {/* Title */}
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: theme.palette.text.primary, lineHeight: 1.3 }}
            >
              Message Content
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.25 }}>
              Configure content used when generating AI messages for each operation type.
            </Typography>
          </Box>

          {/* Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
            {saveAllError && (
              <Typography variant="caption" sx={{ color: theme.palette.error.main, maxWidth: 260 }}>
                {saveAllError}
              </Typography>
            )}
            <ButtonMAIQ
              variant="outlined"
              color="secondary"
              onClick={handleCancelAll}
              disabled={isSavingAll || !isDirty}
              sx={{ height: 40, minWidth: 90 }}
            >
              Cancel
            </ButtonMAIQ>
            <ButtonMAIQ
              variant="contained"
              color="primary"
              onClick={handleSaveAll}
              disabled={isSavingAll || !isDirty}
              startIcon={
                isSavingAll ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <SaveOutlinedIcon fontSize="small" />
                )
              }
              sx={{ height: 40, minWidth: 150 }}
            >
              {isSavingAll ? 'Saving...' : 'Save Changes'}
            </ButtonMAIQ>
          </Box>
        </Box>

        <Divider sx={{ mt: 1.5 }} />
      </Box>

      {/* ── Operation Accordions ── */}
      <Box>
        {messageContent.operations.map((operation, index) => {
          const opLocalState = localOperations[operation.type] || {
            dealer_values: [],
            dealer_specials: [],
            footer_image: [],
          };
          return (
            <OperationAccordion
              key={operation.type}
              operation={operation}
              localState={opLocalState}
              allDealerValues={allDealerValues}
              allDealerSpecials={allDealerSpecials}
              allFooterImages={allFooterImages}
              isLoadingAvailable={isLoadingAvailable}
              messageContentId={`${messageContent._id}-${resetKey}`}
              defaultExpanded={false}
              onDealerValuesUpdate={ids =>
                handleOperationFieldUpdate(operation.type, 'dealer_values', ids)
              }
              onDealerSpecialsUpdate={ids =>
                handleOperationFieldUpdate(operation.type, 'dealer_specials', ids)
              }
              onFooterImagesUpdate={ids =>
                handleOperationFieldUpdate(operation.type, 'footer_image', ids)
              }
            />
          );
        })}
      </Box>
    </Box>
  );
};

export default MessageContentTab;
